import {
  Listbox as HListBox,
  ListboxButton,
  Transition,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Fragment } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "../../../utils/cn";
import {
  formControlVariants,
  FormControlVariantsProps,
  formTextSizes,
} from "../classes";

export interface ListboxOption<T extends string | number> {
  id: T;
  name: string;
}

export type ListBoxProps<T extends string | number> = {
  options: ListboxOption<T>[];
  selectedOption: ListboxOption<T> | null | undefined;
  onOptionChange: (option: ListboxOption<T> | null | undefined) => void;
  className?: string;
  error?: boolean;
  children?: React.ReactNode;
  label?: string;
  "data-testid"?: string;
} & FormControlVariantsProps;

export function ListBox<T extends string | number>({
  selectedOption,
  options,
  onOptionChange,
  className,
  vSize,
  vColor,
  children,
  error,
  label,
  "data-testid": dataTestId,
}: ListBoxProps<T>) {
  const styles = formControlVariants({ vSize, vColor });
  const textSizes = formTextSizes({ vSize });
  const testId = dataTestId || "listbox";

  return (
    <HListBox onChange={onOptionChange} value={selectedOption}>
      {({ open }) => (
        <div className="relative" data-testid={testId}>
          {children}
          <ListboxButton
            aria-label={label}
            className={cn(
              "relative w-full cursor-default",
              "bg-white py-1.5 pr-10 pl-3 text-left",
              "text-gray-900",
              styles,
              error && "ring-control-ring-error",
              className,
            )}
            data-testid="listbox-button"
          >
            {({ value }) => (
              <>
                <span className="block truncate">{value?.name}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronsUpDownIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-gray-400"
                  />
                </span>
              </>
            )}
          </ListboxButton>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={open}
          >
            <ListboxOptions
              className={cn(
                "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1",
                "ring-opacity-5 shadow-popover border-popover-outline border focus:outline-none",
                textSizes,
              )}
            >
              {options.map((option) => (
                <ListboxOption
                  className={cn("mx-2 cursor-default py-1 select-none")}
                  key={option.id}
                  value={option}
                  data-testid={`listbox-option-${option.id}`}
                >
                  {({ focus, active }) => (
                    <div
                      className={cn(
                        "relative flex items-center justify-between rounded px-5 py-2",
                        "rounded",
                        active && "ring-1 ring-black",
                        focus ? "font-semibold" : "font-normal",
                        "block truncate",
                      )}
                    >
                      {option.name}
                      {focus ? (
                        <span
                          className={cn(
                            "text-green-700",
                            "absolute inset-y-0 right-0 flex items-center pr-4",
                          )}
                        >
                          <CheckIcon aria-hidden="true" className="h-5 w-5" />
                        </span>
                      ) : null}
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </HListBox>
  );
}
